import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * Shows a term result exactly as the school designed it in Sheet Designer
 * (the class's own template, or the all-classes one). Falls back to `fallback` when the school has no template yet.
 */
export function TemplateSheet({ url, fallback }: { url: string; fallback: ReactNode }) {
  const q = useQuery({ queryKey: ['result-sheet-html', url], queryFn: () => api.get(url).then((r) => r.data?.data as { html: string | null; template: string | null }), retry: false });
  const frame = useRef<HTMLIFrameElement>(null);
  const [h, setH] = useState(900);

  useEffect(() => {
    const f = frame.current;
    if (!f) return;
    const fit = () => { const d = f.contentDocument; if (d?.body) setH(Math.max(400, d.documentElement.scrollHeight + 16)); };
    f.addEventListener('load', fit);
    const t = setInterval(fit, 800);
    return () => { f.removeEventListener('load', fit); clearInterval(t); };
  }, [q.data?.html]);

  if (q.isLoading) return <p className="text-sm text-slate-500">Loading the report sheet…</p>;
  if (!q.data?.html) return <>{fallback}</>;
  return (
    <div className="-mx-1 sm:mx-0 overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <iframe ref={frame} title="Report sheet" srcDoc={q.data.html} className="w-full min-w-[720px] bg-white" style={{ height: h, border: 0 }} sandbox="allow-same-origin" />
    </div>
  );
}
